import { useEffect, useState } from 'react';

import dayjs from 'dayjs';
import { CheckIcon } from 'lucide-react';
import { DateRange } from 'react-day-picker';

import { Calendar } from '@/components/ui/calendar';
import { Command, CommandItem, CommandList, CommandSeparator } from '@/components/ui/command';
import { Label } from '@/components/ui/label';
import { createDateRangeToStringFormatter } from '@/helpers';
import { formatDate as formatDateLib } from '@/lib/format';
import { cn } from '@/lib/utils';
import { DateRangeFilterOption } from '@/types/filters';

/** Extended DateRange type that allows null for 'to' to represent "no end date" (Ongoing) */
export type DateRangeWithOptionalEnd = {
  from?: Date;
  to?: Date | null;
};

/** Number of years before/after the current year offered in the year dropdown. */
const YEAR_DROPDOWN_RANGE = 20;

export interface DateCalendarProps {
  options?: DateRangeFilterOption[];
  onChange: (dateRange: DateRangeWithOptionalEnd | null) => void;
  value: DateRangeWithOptionalEnd | null;
  footer?: React.ReactElement;
  numberOfmonths?: 1 | 2 | 3 | 4 | 5 | 6;
  disableRangeSelection?: boolean;
  /** Matcher for non-selectable days (e.g. dates before a minimum). */
  disabled?: (date: Date) => boolean;
  /** First day of the week (0 = Sunday … 1 = Monday). Defaults to Monday. */
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
}

export const DateRangeCalendar = ({
  options,
  onChange,
  value,
  footer,
  numberOfmonths = 1,
  disableRangeSelection = false,
  disabled,
  weekStartsOn = 1,
}: DateCalendarProps) => {
  // Internal state uses DateRange (to: undefined) for calendar compatibility
  // External value/onChange uses DateRangeWithOptionalEnd (to: null for "no end date")
  const [localRange, setLocalRange] = useState<DateRange>(() => {
    if (!value) return { from: undefined, to: undefined };
    // Convert null to undefined for internal state
    return { from: value.from, to: value.to ?? undefined };
  });

  const [isSelectingStart, setIsSelectingStart] = useState(true);

  useEffect(() => {
    if (!value) {
      setLocalRange({ from: undefined, to: undefined });
      return;
    }
    // Convert null to undefined for internal state
    setLocalRange({ from: value.from, to: value.to ?? undefined });
  }, [value?.from?.getTime(), value?.to?.getTime()]);

  function handleDayClick(date: Date) {
    // Ignore clicks on disabled days (e.g. dates before the allowed minimum).
    if (disabled?.(date)) return;

    // If range selection is disabled (no end date mode), only update the start date
    if (disableRangeSelection) {
      const newRange = { from: date, to: undefined };
      setLocalRange(newRange);
      // Pass null to onChange to indicate "no end date" (Ongoing)
      onChange({ from: date, to: null });
      return;
    }

    // If starting a new range or both dates are set, set both from and to to the selected date
    if (
      isSelectingStart ||
      (localRange.from && localRange.to && localRange.from?.getTime() !== localRange.to?.getTime())
    ) {
      const newRange = { from: date, to: date };
      setLocalRange(newRange);
      setIsSelectingStart(false);
      onChange(newRange);
      return;
    }
    // If only start is set, set end date
    const newRange = { from: localRange.from, to: date };
    // Ensure from is before to
    if (newRange.from && newRange.to && dayjs(newRange.to).isBefore(newRange.from)) {
      newRange.to = newRange.from;
      newRange.from = date;
    }
    setLocalRange(newRange);
    setIsSelectingStart(true);
    onChange(newRange);
  }

  const hasOptions = Boolean(options?.length);

  const formatDate = date =>
    formatDateLib(date, { month: 'short', day: 'numeric', year: 'numeric' });

  const dateRangeToString = createDateRangeToStringFormatter(formatDate);

  const getIsSelected = (dateRange: DateRange) => {
    if (!value) return false;
    // Convert value to DateRange format for comparison
    const valueAsDateRange: DateRange = { from: value.from, to: value.to ?? undefined };
    return dateRangeToString(valueAsDateRange) === dateRangeToString(dateRange);
  };

  return (
    <Command>
      {hasOptions && (
        <>
          <CommandList className="py-1">
            {options?.map(opt => {
              const isSelected = getIsSelected(opt.value);

              return (
                <CommandItem
                  key={opt.label}
                  onSelect={() => onChange(opt.value)}
                  className={cn(
                    'flex items-center justify-between cursor-pointer px-2 py-1.5',
                    isSelected && 'bg-accent'
                  )}
                >
                  <Label className="font-normal">{opt.label}</Label>
                  {/* TODO: Adjust icon color */}
                  {isSelected && <CheckIcon />}
                </CommandItem>
              );
            })}
          </CommandList>
          <CommandSeparator />
        </>
      )}
      <div>
        <Calendar
          mode="range"
          fixedWeeks
          weekStartsOn={weekStartsOn}
          numberOfMonths={numberOfmonths}
          selected={localRange}
          defaultMonth={localRange.from}
          onDayClick={handleDayClick}
          disabled={disabled}
          // Month + year dropdown navigation so users can jump to a distant
          // month/year without paging through arrows (OPS-1522).
          captionLayout="dropdown"
          startMonth={new Date(new Date().getFullYear() - YEAR_DROPDOWN_RANGE, 0)}
          endMonth={new Date(new Date().getFullYear() + YEAR_DROPDOWN_RANGE, 11)}
          onSelect={range => {
            // Need to keep this method in order to keep date selector reset working
            if (!range?.from) return;
          }}
        />
      </div>
      {footer && (
        <>
          <CommandSeparator />
          {footer}
        </>
      )}
    </Command>
  );
};
